import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { DEFAULT_CURRENCY_CODE } from 'shared/enums/SitePreferences';
import { FILTER_ENUMS, SORT, SORT_FILTER_BUTTON_CLASSNAME } from 'search/enums/constants';
import {
  IConstructorFacet,
  IConstructorFacetMultiple,
  isIConstructorFacetMultiple,
  isIConstructorFacetRange,
} from 'shared/types/ConstructorFacet';
import { IConstructorGroup } from 'shared/types/ConstructorGroup';
import { IConstructorSortOption } from 'shared/types/ConstructorSortOption';
import {
  ISearchMultiSelect,
  SearchMultiSelect,
} from 'search/components/searchGrid/SearchMultiSelect';
import { SearchBoxButton, SearchFilterPillStyle } from 'search/styles/SearchStyles';
import {
  ISearchFilterCategoryMobile,
  SearchFilterCategoryMobile,
} from 'search/components/searchGrid/SearchFilterCategoryMobile';
import {
  ISearchFilterPriceMobile,
  SearchFilterPriceMobile,
} from 'search/components/searchGrid/SearchFilterPriceMobile';
import { SearchFilterPriceBucketMobile } from 'search/components/searchGrid/SearchFilterPriceBucketMobile';
import { SearchFilterSizeMobile } from 'search/components/searchGrid/SearchFilterSizeMobile';
import { SearchFilterYearMobile } from 'search/components/searchGrid/SearchFilterYearMobile';
import { SearchSingleSelect } from 'search/components/searchGrid/SearchSingleSelect';
import { getInitialYearBucketState } from 'search/utils/mobileYearHelpers';
import { getIsSortActive } from 'search/utils/getIsSortActive';
import {
  getAvailableNowFacet,
  getBrandFacet,
  getCategoryFacet,
  getColorFacet,
  getConditionFacet,
  getDiscountTagFacet,
  getGenderFacet,
  getInstantShipFacet,
  getMobilePriceFacet,
  getPriceBucketFacet,
  getSizeFacet,
  getYearBucketFacet,
} from 'search/utils/getFacets';
import { getInitialRangeStateFromFacet } from 'search/utils/getInitialRangeStateFromFacet';
import { handleSortQueryParams } from 'search/utils/handleSortQueryParams';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useShowFilterState } from 'search/hooks/useShowFilterState';
import { SearchFilterBrandCrawler } from 'search/components/searchGrid/SearchFilterBrandCrawler';
import { SearchFilterCategoryMobileCrawler } from 'search/components/searchGrid/SearchFilterCategoryMobileCrawler';
import { getConstructorCategoriesStrings } from 'search/enums/translations';
import { useIsCrawler } from 'shared/hooks/useIsCrawler';
import { t } from 'localization/utils/t';

const TP = 'search.components.searchGrid.SearchSortAndFiltersMobile';

const SearchFilterInstantShip = dynamic(
  () =>
    import('search/components/searchGrid/SearchFilterInstantShip').then(
      (mod) => mod.SearchFilterInstantShip,
    ),
  { ssr: true },
);
const SearchFilterDiscountAndAvailableNow = dynamic(
  () =>
    import('search/components/searchGrid/SearchFilterDiscountAndAvailableNow').then(
      (mod) => mod.SearchFilterDiscountAndAvailableNow,
    ),
  { ssr: true },
);
const SearchSortAndFiltersModal = dynamic(
  () => import('search/components/searchGrid/SearchSortAndFiltersModal'),
  { ssr: false },
);

interface ISearchSortAndFiltersMobile {
  facets: Array<IConstructorFacet>;
  groups: IConstructorGroup;
  resultIds: Array<string>;
  sortOptions: Array<IConstructorSortOption>;
  isCrawler?: boolean;
}

export const SearchSortAndFiltersMobile = ({
  facets,
  groups,
  resultIds,
  sortOptions,
  isCrawler = false,
}: ISearchSortAndFiltersMobile) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeView, setActiveView] = useState('');
  const initialSelectedSort = sortOptions.find((option) => option.status === 'selected');
  const [selectedSortOption, setSelectedSortOption] = useState(initialSelectedSort);

  const {
    showAvailableNowFilter,
    showBrandFilter,
    showCategoryFilter,
    showColorFilter,
    showConditionFilter,
    showDiscountTagFilter,
    showGenderFilter,
    showInstantFilter,
    showPriceFilter,
    showSizeFilter,
    showYearFilter,
  } = useShowFilterState(isCrawler);
  const hasNestedCategories = groups?.children?.length > 0;
  const topLevelCategory = groups?.group_id;
  const instantShipFacet = showInstantFilter && getInstantShipFacet(facets);
  const availableNowFacet = showAvailableNowFilter && getAvailableNowFacet(facets);
  const discountTagFacet = showDiscountTagFilter && getDiscountTagFacet(facets);
  const eventTrackingEventContext = useEventContext();
  const router = useRouter();
  const { pathname, query } = router;
  const { query: searchQuery, release_date: releaseDate, sortBy, sortOrder } = query;
  const { selectedCurrencyCode } = useCurrency();
  const { doNotCrawlPaths } = useIsCrawler();
  const isUsd = selectedCurrencyCode === DEFAULT_CURRENCY_CODE;
  const forCrawler = isCrawler && !doNotCrawlPaths;

  const FILTER_MAP = {
    [FILTER_ENUMS.BRAND]: {
      component: SearchMultiSelect,
      facet: showBrandFilter && getBrandFacet(facets),
      columns: [t(`${TP}.location`, 'Location'), t(`${TP}.items`, 'Items')],
      row: 1,
    },
    [FILTER_ENUMS.CATEGORY]: {
      component: SearchFilterCategoryMobile,
      facet: showCategoryFilter && hasNestedCategories && getCategoryFacet(facets),
      columns: [t(`${TP}.items`, 'Items')],
      row: 1,
    },
    [FILTER_ENUMS.GENDER]: {
      component: SearchMultiSelect,
      facet: showGenderFilter && getGenderFacet(facets),
      columns: [t(`${TP}.items`, 'Items')],
      row: 1,
    },
    [FILTER_ENUMS.SIZE]: {
      facet: showSizeFilter && getSizeFacet(facets),
      row: 1,
    },
    ...(!isUsd && {
      [FILTER_ENUMS.PRICE]: {
        component: SearchFilterPriceMobile,
        facet: showPriceFilter && getMobilePriceFacet(facets),
        row: 1,
      },
    }),
    ...(isUsd && {
      [FILTER_ENUMS.PRICE_BUCKET]: {
        component: SearchFilterPriceBucketMobile,
        facet: showPriceFilter && getPriceBucketFacet(facets),
        row: 1,
      },
    }),
    [FILTER_ENUMS.CONDITION]: {
      component: SearchMultiSelect,
      facet: showConditionFilter && getConditionFacet(facets),
      columns: [t(`${TP}.items`, 'Items')],
      row: 1,
    },
    [FILTER_ENUMS.COLOR]: {
      component: SearchMultiSelect,
      facet: showColorFilter && getColorFacet(facets),
      columns: [t(`${TP}.items`, 'Items')],
      row: 2,
    },
    [FILTER_ENUMS.YEAR_BUCKET]: {
      component: SearchFilterYearMobile,
      facet: showYearFilter && getYearBucketFacet(facets),
      row: 2,
    },
  };

  const [selectedFilterValues, setSelectedFilterValues] = useState(null);
  const ModalContent: FC<
    ISearchMultiSelect & ISearchFilterPriceMobile & ISearchFilterCategoryMobile
  > = FILTER_MAP[activeView]?.component;
  const filterRowDiv = document.querySelector(`#row-filter-${FILTER_MAP?.[activeView]?.row || 1}`);
  const modalTopPosition = filterRowDiv?.getBoundingClientRect()?.y;
  const activeViewFacet = FILTER_MAP[activeView]?.facet;

  const getQueryParams = () => {
    if (activeView === FILTER_ENUMS.YEAR_BUCKET) {
      return selectedFilterValues;
    }

    return {
      ...(activeView === SORT
        ? handleSortQueryParams(selectedSortOption)
        : { [activeView]: selectedFilterValues }),
    };
  };

  useEffect(() => {
    const initialSelectedFilterValues =
      isIConstructorFacetMultiple(activeViewFacet) &&
      activeViewFacet?.options
        ?.filter((option) => option.status === 'selected')
        ?.map((option) => option.value);
    const priceFacet = FILTER_MAP[FILTER_ENUMS.PRICE]?.facet;
    const yearBucketFacet = FILTER_MAP[FILTER_ENUMS.YEAR_BUCKET]?.facet;

    if (activeView === FILTER_ENUMS.PRICE && priceFacet) {
      const initialPriceState =
        isIConstructorFacetRange(priceFacet) && getInitialRangeStateFromFacet(priceFacet);

      setSelectedFilterValues(initialPriceState);
    } else if (activeView === FILTER_ENUMS.YEAR_BUCKET && yearBucketFacet) {
      const releaseDateQuery = query?.[FILTER_ENUMS.RELEASE_DATE];
      const yearBucketQuery = query?.[FILTER_ENUMS.YEAR_BUCKET];
      const initialYearBucketState = getInitialYearBucketState(yearBucketFacet);

      setSelectedFilterValues({
        ...(yearBucketQuery && { [FILTER_ENUMS.YEAR_BUCKET]: initialYearBucketState }),
        ...(releaseDateQuery && {
          [FILTER_ENUMS.RELEASE_DATE]: releaseDateQuery,
        }),
        // Check if it is Upcoming Releases in mobile Years is selected
        ...(releaseDateQuery &&
          sortBy === 'release_date' &&
          sortOrder === 'ascending' && {
            sortBy: 'release_date',
            sortOrder: 'ascending',
          }),
      });
    } else {
      setSelectedFilterValues(initialSelectedFilterValues || []);
    }
  }, [activeViewFacet]);

  useEffect(() => {
    // This is to account for when a price sort has been applied
    // and user changes location, and so now the price sort `lowest_price_cents_3` for global pricing
    // no longer exists, so we default to Popular sort.
    // BUY3-2027: We need to check for if releaseDate exists because when Upcoming Releases is selected
    // from the Years filter on mobile no selectedSortOption is set initially and that's desired behavior
    if (!selectedSortOption && !releaseDate) {
      void router.push({
        pathname,
        query: { ...query, sortBy: 'relevance', sortOrder: 'ascending' },
      });
    }
  }, [selectedSortOption]);

  const openModal = (view) => {
    setActiveView(view);
    setIsModalOpen(true);
    sendTrackingEvent('FILTER_EXPAND_TAP', {
      event_context: eventTrackingEventContext,
      filter_name: view,
      ...(searchQuery && { search_string: searchQuery.toString() }),
    });
  };

  const closeModal = () => {
    setActiveView('');
    setIsModalOpen(false);
  };

  const getIsActive = (facet) => {
    const isCurrentlyOpen = activeView === facet?.name;
    const hasFiltersApplied =
      facet?.name !== FILTER_ENUMS.PRICE &&
      facet?.options?.some((option) => option.status === 'selected');
    const isPriceFilterApplied =
      facet?.name === FILTER_ENUMS.PRICE && Object.keys(facet?.status).length === 2;
    // Accounts for Upcoming Releases under Year filter for mobile
    const isYearFilterApplied =
      facet.name === FILTER_ENUMS.YEAR_BUCKET &&
      query?.[FILTER_ENUMS.RELEASE_DATE]?.length > 1 &&
      sortBy === 'release_date' &&
      sortOrder === 'ascending';

    return isCurrentlyOpen || hasFiltersApplied || isPriceFilterApplied || isYearFilterApplied;
  };

  return (
    <>
      <SearchSortAndFiltersMobile.FirstRowFilters id="row-filter-1">
        <SearchSortAndFiltersMobile.SortAndFilterButton
          onClick={() => openModal(SORT)}
          className={SORT_FILTER_BUTTON_CLASSNAME}
          $isActive={getIsSortActive(activeView, router)}
          data-qa="mobile_sort"
        >
          {t(`${TP}.sort`, 'Sort')}
        </SearchSortAndFiltersMobile.SortAndFilterButton>
        {Object.keys(FILTER_MAP).map(
          (filter) =>
            FILTER_MAP[filter].facet &&
            FILTER_MAP[filter].row === 1 && (
              <SearchSortAndFiltersMobile.SortAndFilterButton
                onClick={() => openModal(FILTER_MAP[filter].facet?.name)}
                className={SORT_FILTER_BUTTON_CLASSNAME}
                $isActive={getIsActive(FILTER_MAP[filter].facet)}
                data-qa={`mobile_${FILTER_MAP[filter].facet?.name}_filter`}
                key={FILTER_MAP[filter].facet?.name}
              >
                {getConstructorCategoriesStrings(FILTER_MAP[filter].facet?.display_name)}
              </SearchSortAndFiltersMobile.SortAndFilterButton>
            ),
        )}
      </SearchSortAndFiltersMobile.FirstRowFilters>
      <SearchSortAndFiltersMobile.SecondRowFilters id="row-filter-2">
        {Object.keys(FILTER_MAP).map(
          (filter) =>
            FILTER_MAP[filter].facet &&
            FILTER_MAP[filter].row === 2 && (
              <SearchSortAndFiltersMobile.SortAndFilterButton
                onClick={() => openModal(FILTER_MAP[filter].facet?.name)}
                className={SORT_FILTER_BUTTON_CLASSNAME}
                $isSelected={getIsActive(FILTER_MAP[filter].facet)}
                $isPillStyle
                data-qa={`mobile_${FILTER_MAP[filter].facet?.name}_filter`}
                key={FILTER_MAP[filter].facet?.name}
              >
                {getConstructorCategoriesStrings(FILTER_MAP[filter].facet?.display_name)}
              </SearchSortAndFiltersMobile.SortAndFilterButton>
            ),
        )}
        {instantShipFacet && (
          <SearchFilterInstantShip facet={instantShipFacet} resultIds={resultIds} />
        )}
        {(discountTagFacet || availableNowFacet) && (
          <SearchFilterDiscountAndAvailableNow
            availableNowFacet={availableNowFacet}
            discountTagFacet={discountTagFacet}
            resultIds={resultIds}
          />
        )}
      </SearchSortAndFiltersMobile.SecondRowFilters>

      {isModalOpen && (
        <SearchSortAndFiltersModal
          closeModal={closeModal}
          columns={activeView === SORT ? [] : FILTER_MAP[activeView]?.columns}
          hideHeader={[
            FILTER_ENUMS.SIZE,
            FILTER_ENUMS.GENDER,
            FILTER_ENUMS.PRICE,
            FILTER_ENUMS.PRICE_BUCKET,
            FILTER_ENUMS.YEAR_BUCKET,
            SORT,
          ].includes(activeView)}
          modalTopPosition={modalTopPosition}
          queryParams={getQueryParams()}
          resultIds={resultIds}
          title={getConstructorCategoriesStrings(FILTER_MAP[activeView]?.facet?.display_name)}
        >
          {activeView === SORT && (
            <SearchSingleSelect
              name={SORT}
              options={sortOptions}
              selectedOption={selectedSortOption}
              setSelectedOption={setSelectedSortOption}
            />
          )}
          {activeView === FILTER_ENUMS.SIZE && (
            <SearchFilterSizeMobile
              facet={FILTER_MAP[activeView].facet as IConstructorFacetMultiple}
              selectedFilterValues={selectedFilterValues}
              setSelectedFilterValues={setSelectedFilterValues}
            />
          )}
          {ModalContent && FILTER_MAP[activeView]?.facet && (
            <ModalContent
              facet={FILTER_MAP[activeView].facet as any}
              name={FILTER_MAP[activeView].facet?.name}
              numColumns={FILTER_MAP[activeView]?.columns?.length + 2}
              selectedFilterValues={selectedFilterValues}
              setSelectedFilterValues={setSelectedFilterValues}
              topLevelCategory={topLevelCategory}
            />
          )}
        </SearchSortAndFiltersModal>
      )}

      {forCrawler && (
        <SearchSortAndFiltersMobile.CrawlerFilters>
          <SearchFilterBrandCrawler
            facet={FILTER_MAP.brand.facet as any}
            name={FILTER_MAP.brand.facet?.name}
          />
        </SearchSortAndFiltersMobile.CrawlerFilters>
      )}

      {forCrawler && (
        <SearchSortAndFiltersMobile.CrawlerFilters>
          <SearchFilterCategoryMobileCrawler
            facet={FILTER_MAP.web_groups.facet as any}
            topLevelCategory={topLevelCategory}
          />
        </SearchSortAndFiltersMobile.CrawlerFilters>
      )}
    </>
  );
};

SearchSortAndFiltersMobile.SortAndFilterButton = styled(SearchBoxButton)<{
  $isActive?: boolean;
  $isSelected?: boolean;
  $isPillStyle?: boolean;
}>`
  text-transform: capitalize;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  border: 1px solid ${({ theme }) => theme.navigation.borderColor};
  color: ${({ theme }) => theme.navigation.textColor};

  ${({ $isActive }) =>
    $isActive &&
    css`
      color: ${({ theme }) => theme.navigation.backgroundColor};
      background-color: ${({ theme }) => theme.navigation.loaderColor};
    `}

  ${({ $isPillStyle }) =>
    $isPillStyle &&
    css`
      ${SearchFilterPillStyle};
    `};

  :first-child {
    margin-left: 16px;
  }

  :last-child {
    margin-right: 16px;
  }

  :hover {
    text-decoration: none;
  }
`;

SearchSortAndFiltersMobile.FirstRowFilters = styled.div`
  display: flex;
`;

SearchSortAndFiltersMobile.SecondRowFilters = styled.div`
  display: flex;
  margin-top: 8px;
`;

SearchSortAndFiltersMobile.CrawlerFilters = styled.div`
  display: none;
`;
